import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'sputnik-ui'
import CountBadge from 'components/atoms/CountBadge'
import palette from 'theme/palette'
import { fetchReleaseNotes, fetchUnreadCount } from '../model/releaseNotesSlice'

const WidgetRoot = styled.div`
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 1300;
`

const Fab = styled.button`
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 50%;
  background: ${palette.accent.color};
  color: #ffffff;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(14, 167, 255, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`

const WidgetCountBadge = styled(CountBadge)`
  position: absolute;
  right: -4px;
  top: -4px;
  min-width: 22px;
  height: 22px;
`

const Panel = styled.div`
  position: absolute;
  right: 0;
  bottom: 72px;
  width: 360px;
  background: #ffffff;
  border: 1px solid #ededed;
  border-radius: 14px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.16);
  overflow: hidden;
`

const PanelHeader = styled.div`
  padding: 14px 16px;
  font-weight: 700;
  font-size: 16px;
  color: #202020;
  border-bottom: 1px solid #ededed;
`

const PanelList = styled.div`
  max-height: 320px;
  overflow-y: auto;
`

const PanelItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #f3f3f4;
  cursor: pointer;
  &:hover {
    background: #fafafa;
  }
`

const ItemTitle = styled.div`
  color: #202020;
  font-size: 14px;
  font-weight: 600;
`

const ItemSummary = styled.div`
  color: #6d6d6d;
  font-size: 13px;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PanelFooter = styled.div`
  padding: 12px 16px;
`

const CtaButton = styled(Button)`
  width: 100%;
  border-radius: 22px;
`

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: #6d6d6d;
  font-size: 14px;
`

const ReleaseWidget = () => {
  const { t } = useTranslation(['release_notes'])
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const items = useSelector(({ releaseNotes }) => releaseNotes.items)
  const unreadCount = useSelector(
    ({ releaseNotes }) => releaseNotes.unreadCount
  )
  const history = useHistory()

  useEffect(() => {
    const load = () => {
      // Фоновые вкладки не поллят: каждая лишняя вкладка — лишний кандидат на
      // 401 и SSO-круг. Счётчик догоняет при возврате видимости.
      if (document.hidden) return
      dispatch(fetchUnreadCount())
    }
    load()
    const interval = setInterval(load, 60000)
    document.addEventListener('visibilitychange', load)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', load)
    }
  }, [dispatch])

  useEffect(() => {
    if (open) {
      dispatch(fetchReleaseNotes({ page: 1, per_page: 5 }))
    }
  }, [dispatch, open])

  const handleToggle = () => {
    setOpen((prev) => !prev)
  }

  const handleGoToCenter = () => {
    setOpen(false)
    history.push('/whats-new')
  }

  return (
    <WidgetRoot>
      {open && (
        <Panel>
          <PanelHeader>
            {t('release_notes:widget.header', 'Обновления')}
          </PanelHeader>
          <PanelList>
            {items.length === 0 ? (
              <EmptyState>
                {t('release_notes:widget.empty', 'Нет новых обновлений')}
              </EmptyState>
            ) : (
              items.map((item) => (
                <PanelItem key={item.id} onClick={handleGoToCenter}>
                  <ItemTitle>{item.title}</ItemTitle>
                  <ItemSummary>{item.summary}</ItemSummary>
                </PanelItem>
              ))
            )}
          </PanelList>
          <PanelFooter>
            <CtaButton variant="v2" primary onClick={handleGoToCenter}>
              {t('release_notes:widget.cta', 'Перейти в «Что нового»')}
            </CtaButton>
          </PanelFooter>
        </Panel>
      )}
      <Fab onClick={handleToggle}>
        {unreadCount > 0 && <WidgetCountBadge>{unreadCount}</WidgetCountBadge>}
        {open ? '✕' : '🔔'}
      </Fab>
    </WidgetRoot>
  )
}

export default ReleaseWidget
